/* eslint-disable react/destructuring-assignment */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import _ from 'lodash';

import withJsonLd from '@nerdwallet/json-ld';
import { getArticleSchema } from '@nerdwallet/json-ld/mappings/wp/article';
import { getNerdlayoutDynamicOptions } from '@nerdwallet/wp-api/utils/meta-data';
import { Box } from '@nerdwallet/currency';

import GlobalMarkupOptions from '@nerdwallet/coin/GlobalMarkupOptions';

import { Location } from 'history';
import {
  getArticleTypeConfig,
  getArticleCanonicalUrl,
} from '~/lib/article-utils';
import { appendEmbedJsScriptToDocument, renderEmbeds } from '~/lib/embed';
import ABConfig, { Experiments, getActiveExperiments } from '~/lib/ab';

import ArticleStructuredContent from '~/client/components/article-structured-content/article-structured-content';
import WpEditLink from '../../components/wp-edit-link/wp-edit-link';
import Breadcrumb from './breadcrumb';
import {
  ArticleFragmentFragment,
  GetArticleSchemaFragment,
} from '~/generated-gql/generated-types';
import { ArticleTypeConfig, ReduxStore, RouterPathParams } from '~/lib/types';

import GeneralArticleEditorialTest from '~/client/components/general-article-editorial-test/general-article-editorial-test';

type Props = RouteComponentProps<RouterPathParams> & {
  graphqlArticle: ArticleFragmentFragment;
  loading: boolean;
  location: Location;
  slug: string;
  experiments: Experiments;
  hasWpCookie: boolean;
};

interface GtmEntry {
  articleField: keyof ArticleFragmentFragment;
  gtmVarName: string;
}

// pull in SEO experimental schema
@withJsonLd(
  ({ graphqlArticle }: { graphqlArticle: ArticleFragmentFragment }) =>
    graphqlArticle?.seo?.extraJsonLd
)
@withJsonLd(
  ({ graphqlArticle }: { graphqlArticle: GetArticleSchemaFragment }) =>
    getArticleSchema(graphqlArticle)
)
class ArticleContainer extends Component<Props> {
  articleTypeConfig: ArticleTypeConfig;

  constructor(props: Props) {
    super(props);

    this.articleTypeConfig = getArticleTypeConfig(
      props.graphqlArticle?.articleType
    );
  }

  componentDidMount() {
    // Manually append embed.js since embed <script> tags in the WP
    // API response aren't parsed via dangerouslySetInnerHTML.
    appendEmbedJsScriptToDocument();
  }

  componentDidUpdate() {
    // Rerender embeds if the article is updated and fully loaded.
    if (!this.props.loading) {
      renderEmbeds();
    }
  }

  renderNlOpts() {
    const {
      graphqlArticle,
      location: { pathname },
    } = this.props;

    const canonicalURL = getArticleCanonicalUrl(graphqlArticle, pathname);
    const { contentClusters } = graphqlArticle;

    const optionsOverrides = {
      canonicalURL,
      contentClusters,
    };

    const dynamicOpts = getNerdlayoutDynamicOptions({
      post: graphqlArticle,
      optionsOverrides,
    });

    const globalNavOpts = {
      globalNavProps: {
        primaryNavCategory: 'Guides & Tips',
      },
    };

    // articleField is the actual name of the field on the GraphQL query.
    // gtmVarName is the name expected on googleTagManager integration.
    // See https://github.com/NerdWallet/global-markup/blob/master/src/versions/v2.js#L446
    // and https://github.com/NerdWallet/global-markup/blob/master/src/versions/v2.js#L508
    // For further reference over googleTagManager field names.
    [
      { articleField: 'attributionVertical', gtmVarName: 'attrVertical' },
      { articleField: 'attributionTopic', gtmVarName: 'attrTopic' },
      { articleField: 'primaryContentCluster', gtmVarName: 'primaryCluster' },
    ].forEach(({ articleField, gtmVarName }: GtmEntry) => {
      dynamicOpts.pageTaxonomy?.push({
        nwVarName: gtmVarName,
        value: graphqlArticle[articleField],
      });
    });

    const opts = _.merge(globalNavOpts, dynamicOpts);
    const { hasBreadcrumbs } = this.articleTypeConfig;

    return (
      <React.Fragment>
        {hasBreadcrumbs && <Breadcrumb>{graphqlArticle}</Breadcrumb>}
        <GlobalMarkupOptions>{opts}</GlobalMarkupOptions>
      </React.Fragment>
    );
  }

  renderArticleComponent() {
    let ArticleComponent = this.articleTypeConfig.component;

    const showEditorialPageTest = ABConfig.EDITORIAL_PAGE_TEST.paths?.includes(
      this.props.slug
    );
    if (showEditorialPageTest) {
      ArticleComponent = GeneralArticleEditorialTest;
    }

    const props = {
      slug: this.props.slug,
      graphqlArticle: this.props.graphqlArticle,
      loading: this.props.loading,
      activeExperiments: getActiveExperiments({
        experiments: this.props.experiments,
        location: this.props.location,
        articleType: this.props.graphqlArticle?.articleType,
      }),
    };

    _.assign(props, _.omit(this.articleTypeConfig, ['component']));

    return <ArticleComponent {...props} />;
  }

  render() {
    const { graphqlArticle } = this.props;

    return (
      <Box className="article-container">
        {this.renderNlOpts()}
        {this.props.hasWpCookie && <WpEditLink id={graphqlArticle.id} />}
        {graphqlArticle.heroBannerDocument && (
          <Box marginBottom={2}>
            <ArticleStructuredContent>
              {graphqlArticle.heroBannerDocument}
            </ArticleStructuredContent>
          </Box>
        )}
        {this.renderArticleComponent()}
      </Box>
    );
  }
}

// Ensure we only grab pieces of the state that we need.
const getHasWpCookie = (state: ReduxStore) => state.hasWpCookie;
const getExperiments = (state: ReduxStore) => state.experiments;

const mapStateToProps = (state: ReduxStore) => ({
  hasWpCookie: getHasWpCookie(state),
  experiments: getExperiments(state),
});

export default withRouter(connect(mapStateToProps)(ArticleContainer));
